import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import { SnDrawerService } from '@lib/ui/drawer';

import { DebugPanelRegistryService } from '../services';
import { DebugPanelItem } from '../types';
import { getLastSelectedPanel, setLastSelectedPanel } from '../utils';

@Component({
  selector: 'fs-debug-panel',
  templateUrl: './debug-panel.component.html',
  styleUrls: ['./debug-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebugPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('componentContainer', { read: ViewContainerRef })
  componentContainer!: ViewContainerRef;

  protected readonly _destroy$ = new Subject<void>();
  private _debugPanelItems: DebugPanelItem[] = [];

  public get debugPanelItems(): DebugPanelItem[] {
    return this._debugPanelItems.filter((item) => !item.condition || item.condition());
  }

  private _selectedItemId: string | null = getLastSelectedPanel();

  public get selectedItemId(): string | null {
    return this._selectedItemId;
  }

  public set selectedItemId(value: string | null) {
    this._selectedItemId = value;
    this._renderSelectedComponent();
    setLastSelectedPanel(value);
  }

  constructor(
    private _drawer: SnDrawerService,
    private _cdr: ChangeDetectorRef,
    private _registry: DebugPanelRegistryService,
  ) {}

  public ngOnInit(): void {
    this._registry.items$.pipe(takeUntil(this._destroy$)).subscribe((items) => {
      this._debugPanelItems = items;

      if (!this.debugPanelItems.length) {
        this.selectedItemId = null;
        return;
      }

      const selectedItemInList = this.debugPanelItems.find((item) => {
        return item.id === this.selectedItemId;
      });
      if (this.selectedItemId && !selectedItemInList) {
        this.selectedItemId = null;
      }
      if (!this.selectedItemId && this.debugPanelItems.length > 0) {
        this.selectedItemId = this.debugPanelItems[0].id;
      }
    });
  }

  public ngAfterViewInit(): void {
    if (this.selectedItemId) {
      this._renderSelectedComponent();
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _renderSelectedComponent(): void {
    if (!this.componentContainer) {
      return;
    }
    this.componentContainer.clear();

    if (!this._selectedItemId) {
      return;
    }

    const selectedItem = this.debugPanelItems.find((item) => {
      return item.id === this._selectedItemId;
    });
    if (selectedItem) {
      this.componentContainer.createComponent(selectedItem.component, {
        injector: selectedItem.injector,
      });
      this._cdr.markForCheck();
    }
  }

  public closeDebugPanel(): void {
    this._drawer.close('debug-panel');
  }
}
